import React from 'react'
import { connect } from 'react-redux'

import { ShortlistState } from '@store/actionSlices/shortlist'
import {
  BucketSource,
  GalleryInterface,
  GalleryItemInterface,
  ProjectIdentity,
  RootStateTypeExtra,
  UnitShortlistInterface,
} from '@store/types'

import CarouselHandler from '@components/carousel-handler'
import { PropertiyGallerySkeleton } from '@components/compare-property/skeleton'
import DataHandler from '@components/data-handler'
import { Modal } from '@components/modals'

import { selectFromResult, useGetUnitGalleryQuery } from '@api/unit-gallery'

import { ShortlistPropertyType } from '@utilities/adgroup-utilities/constants'
import ShortlistHandler from '@utilities/shortlist-handler'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { EyeCloseSvg, InfoCircleSvg, StarSvg } from '@svg/react'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { FileType } from '@adUtilities/constants/common'
import mapPropertyGallery, {
  FloorImage,
  PropertyGallery,
} from '@adUtilities/property-gallery'
import { UnitProperty } from '@adUtilities/types'

import UnitSelector from './unit-selector'

const MAXIMUM_PROPERTY_LENGTH_FOR_SHORTLIST =
  Number(process.env.REACT_APP_MAXIMUM_PROPERTY_LENGTH_FOR_SHORTLIST) || 4

export interface ComparableUnitItemProps {
  unit: UnitProperty
  projectIdentity: ProjectIdentity
  showInfoSession?: boolean
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  shortlist: ShortlistState
}

function ComparableUnitItem({
  unit,
  projectIdentity,
  showInfoSession,
  comparePropertyProps,
  shortlist,
}: ComparableUnitItemProps) {
  const { statusLabels, projectName } = projectIdentity
  const availableStatusLabel = statusLabels?.available
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const [showAlertModal, toggleAlertModal] = React.useState(false)
  const [alertModalMessage, setAlertModalMessage] = React.useState('')
  const [showConfirmModal, toggleConfirmModal] = React.useState(false)
  const [removeShortlistProperty, setRemoveShortlistProperty] =
    React.useState(false)

  const [showPropertySelector, setShowPropertySelector] = React.useState(false)

  const { checkPropertyPositionInCompareMode } = comparePropertyProps

  const { onlyShowPropertyGalleries, projectUUID } = projectIdentity

  const unitIndex = React.useMemo(
    () => checkPropertyPositionInCompareMode(unit.data.id)?.index,
    [unit.data.id, checkPropertyPositionInCompareMode]
  )

  const unitGalleryQuery = useGetUnitGalleryQuery(
    { projectName, unitId: unit.data.id },
    { selectFromResult }
  )
  const { unitGalleryData } = unitGalleryQuery

  const buildPropertyGalleryItems = (): PropertyGallery[] =>
    unitGalleryData?.map(({ id, src, label, type, thumbnail }) => ({
      id,
      src,
      thumbnail,
      label,
      type,
      noSpliceUrl: false,
    })) || []

  const buildCarouselItems = (
    propertyGallery: PropertyGallery[]
  ): GalleryItemInterface[] =>
    propertyGallery.map(({ id, label, src, thumbnail, type, noSpliceUrl }) => ({
      id,
      label,
      imageSource: AssetHandler({
        url: type === FileType.Image && src ? src : thumbnail || '',
        type: BucketSource.Legacy,
        noSpliceUrl,
      }),
    }))

  const carouselData = React.useMemo<GalleryInterface>(() => {
    const {
      metas: { floorImage, floorLabel, floorPdf, viewLineImages },
    } = unit.data

    const floorItems: FloorImage[] = floorImage
      ? [
          {
            id: floorPdf,
            src: floorImage,
            label: floorLabel || floorImage,
          },
        ]
      : []

    const propertyGalleryItems = buildPropertyGalleryItems()

    const propertyGallery = mapPropertyGallery({
      projectId: projectUUID,
      floorImages: floorItems,
      viewLineImages: viewLineImages || [],
      propertyGalleries: propertyGalleryItems,
      onlyShowPropertyGallery: Boolean(onlyShowPropertyGalleries),
    })
    const carouselItems = buildCarouselItems(propertyGallery)

    return {
      items: carouselItems[0] ? [carouselItems[0]] : [],
    }
  }, [onlyShowPropertyGalleries, projectUUID, unit.data.metas, unitGalleryData])

  const handleToggleInfo = React.useCallback(async () => {
    await firebaseControlQuery.update({
      [`building.unitInfoPopup`]: !showInfoSession,
    })
  }, [showInfoSession])

  const property = React.useMemo<UnitShortlistInterface>(
    () => ({
      propertyId: unit.data.id,
      propertyName: unit.data.name,
      references: {
        blockId: unit.data.blockId,
        levelId: unit.references.levelId,
      },
      type: ShortlistPropertyType.Unit,
    }),
    [unit]
  )

  const setShortlistErrorMessage = React.useCallback(
    (message: string) => {
      setAlertModalMessage(message)
      toggleAlertModal(true)
    },
    [setAlertModalMessage, toggleAlertModal]
  )

  const [isShortlisted, handleShortlistClick] = ShortlistHandler({
    property,
    shortlist,
    setShortlistErrorMessage,
    projectIdentity,
    toggleConfirmModal,
    removeShortlistProperty,
    setRemoveShortlistProperty,
    available: unit.data.metas.status,
  })

  const disabledShortlist =
    shortlist.shortlists.length >= MAXIMUM_PROPERTY_LENGTH_FOR_SHORTLIST

  return (
    <>
      <div className="flex h-full w-full flex-col gap-[10px]">
        <UnitSelector
          unit={unit}
          unitIndex={unitIndex}
          comparePropertyProps={comparePropertyProps}
          onToggleList={setShowPropertySelector}
        />
        {showPropertySelector ? null : (
          <div className="flex h-full w-full flex-col gap-[15px]">
            <div className="relative h-[60%] w-full rounded-lg">
              <DataHandler
                payloads={{
                  ...unitGalleryQuery,
                  data: carouselData,
                }}
                skeletonFrame={<PropertiyGallerySkeleton />}
              >
                <CarouselHandler
                  carousel={carouselData}
                  imgClass="object-cover !h-auto"
                  activeIndex={0}
                  hasBlurredEffect
                />
              </DataHandler>
            </div>
            <button
              className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-gradient text-center text-subHeading font-medium text-neutralColour shadow-standard ${
                unit.data.metas.status !== availableStatusLabel ||
                disabledShortlist
                  ? 'opacity-60'
                  : ''
              } h-[20%]`}
              type="button"
              onClick={handleShortlistClick}
            >
              <span>Add to shortlist</span>
              {unit.data.metas.status !== availableStatusLabel ||
              disabledShortlist ? (
                <StarSvg className="h-8 w-8 stroke-neutralColour text-transparent" />
              ) : (
                <StarSvg
                  className={`h-8 w-8 ${
                    isShortlisted
                      ? 'stroke-amber-500 text-amber-500'
                      : 'stroke-neutralColour text-transparent'
                  }`}
                  stroke={isShortlisted ? 0 : 2}
                />
              )}
            </button>
            <button
              className="flex h-[20%] items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-gradient text-center text-subHeading font-medium text-neutralColour shadow-standard"
              type="button"
              onClick={handleToggleInfo}
            >
              <span>
                {showInfoSession ? 'Hide information' : 'Show information'}
              </span>
              {showInfoSession ? (
                <EyeCloseSvg
                  className="h-8 w-8 text-mainColour"
                  strokeColor="#FFF7E9"
                />
              ) : (
                <InfoCircleSvg className="h-8 w-8 text-neutralColour" />
              )}
            </button>
          </div>
        )}
      </div>
      <Modal
        title="Message"
        isVisible={showAlertModal}
        toggleModal={toggleAlertModal}
        message={alertModalMessage}
        modalWidth="max-w-lg"
      />

      <Modal
        isVisible={showConfirmModal}
        toggleModal={toggleConfirmModal}
        message="Do you want to remove this item from the shortlist?"
        modalWidth="max-w-lg"
        title="Confirmation"
        applyButtonText="Yes"
        closeButtonText="No"
        handleApply={() => {
          setRemoveShortlistProperty(true)
        }}
      />
    </>
  )
}

export default connect(
  ({
    projectIdentity,
    firestore: { session },
    shortlist,
  }: RootStateTypeExtra) => ({
    showInfoSession: !!session?.building?.unitInfoPopup,
    projectIdentity,
    shortlist,
  })
)(ComparableUnitItem)
